import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { motion } from 'framer-motion';
import { ArrowLeft, CreditCard, Truck } from 'lucide-react';
import { useCartStore } from '../store/cartStore';
import { useOrderStore } from '../store/orderStore';
import { formatPrice, generateOrderId } from '../utils/helpers';
import { sendOrderEmailFallback } from '../services/emailService';
import { UserInfo, Order } from '../types';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Textarea from '../components/ui/Textarea';

const Checkout: React.FC = () => {
  const navigate = useNavigate();
  const { items, getTotal, clearCart } = useCartStore();
  const { setLastOrder } = useOrderStore();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<UserInfo>();

  // Redirect if cart is empty
  if (items.length === 0) {
    navigate('/cart');
    return null;
  }

  const onSubmit = async (userInfo: UserInfo) => {
    setIsSubmitting(true);
    
    try {
      const order: Order = {
        id: generateOrderId(),
        items,
        userInfo,
        total: getTotal() * 1.08, // Including tax
        status: 'pending',
        createdAt: new Date(),
      };



      // Send order email
      const emailSuccess = await sendOrderEmailFallback(order, userInfo);
      
      if (emailSuccess) {
        // Store order in store and localStorage as backup
        setLastOrder(order);
        localStorage.setItem('lastOrder', JSON.stringify(order));
        // Clear cart immediately
        clearCart();
        // Navigate to success page with order details
        navigate('/order-success', { state: { order } });
      } else {
        throw new Error('Failed to send order email');
      }
    } catch (error) {
      console.error('Order submission error:', error);
      alert('There was an error processing your order. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };



  return (
    <div className="min-h-screen bg-secondary-50">
      <div className="container-max section-padding">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/cart')}
              className="p-2 text-secondary-600 hover:text-secondary-900 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-3xl font-serif font-bold text-secondary-900">
              Checkout
            </h1>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Checkout Form */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-xl font-semibold text-secondary-900 mb-6">
                Shipping Information
              </h2>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {/* Name */}
                <Input
                  label="Full Name"
                  {...register('name', { required: 'Name is required' })}
                  error={errors.name?.message}
                />

                {/* Email */}
                <Input
                  label="Email Address"
                  type="email"
                  {...register('email', { 
                    required: 'Email is required',
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: 'Please enter a valid email address'
                    }
                  })}
                  error={errors.email?.message}
                />

                {/* Phone */}
                <Input
                  label="Phone Number (Optional)"
                  type="tel"
                  {...register('phone')}
                  error={errors.phone?.message}
                />

                {/* Address */}
                <Textarea
                  label="Shipping Address"
                  rows={3}
                  {...register('address', { required: 'Address is required' })}
                  error={errors.address?.message}
                  placeholder="Enter your complete shipping address"
                />

                {/* Notes */}
                <Textarea
                  label="Order Notes (Optional)"
                  rows={3}
                  {...register('notes')}
                  error={errors.notes?.message}
                  placeholder="Any special instructions or requests..."
                />

                {/* Submit Button */}
                <Button
                  type="submit"
                  loading={isSubmitting}
                  disabled={isSubmitting}
                  className="w-full"
                >
                  {isSubmitting ? 'Processing Order...' : 'Place Order'}
                </Button>
              </form>
            </div>
          </motion.div>

          {/* Order Summary */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="bg-white rounded-xl shadow-lg p-6 sticky top-24">
              <h2 className="text-xl font-semibold text-secondary-900 mb-6">
                Order Summary
              </h2>

              {/* Order Items */}
              <div className="space-y-4 mb-6">
                {items.map((item) => (
                  <div key={item.product.id} className="flex items-center space-x-3">
                    <img
                      src={item.product.images[0]}
                      alt={item.product.title}
                      className="w-12 h-12 object-cover rounded-lg"
                    />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-secondary-900 truncate">
                        {item.product.title}
                      </h3>
                      <p className="text-sm text-secondary-500">
                        Qty: {item.quantity}
                      </p>
                    </div>
                    <p className="font-semibold text-secondary-900">
                      {formatPrice(item.product.price * item.quantity)}
                    </p>
                  </div>
                ))}
              </div>

              {/* Price Breakdown */}
              <div className="border-t border-secondary-200 pt-4 space-y-3">
                <div className="flex justify-between">
                  <span className="text-secondary-600">Subtotal</span>
                  <span className="font-semibold">{formatPrice(getTotal())}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-secondary-600">Shipping</span>
                  <span className="font-semibold text-green-600">Free</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-secondary-600">Tax</span>
                  <span className="font-semibold">{formatPrice(getTotal() * 0.08)}</span>
                </div>
                <div className="border-t border-secondary-200 pt-3">
                  <div className="flex justify-between">
                    <span className="text-lg font-semibold text-secondary-900">Total</span>
                    <span className="text-lg font-bold text-primary-600">
                      {formatPrice(getTotal() * 1.08)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Shipping Info */}
              <div className="mt-6 p-4 bg-secondary-50 rounded-lg">
                <div className="flex items-center space-x-3 mb-2">
                  <Truck className="w-5 h-5 text-primary-600" />
                  <span className="font-medium text-secondary-900">Free Shipping</span>
                </div>
                <p className="text-sm text-secondary-600">
                  Standard delivery: 5-7 business days
                </p>
              </div>

              {/* Payment Info */}
              <div className="mt-4 p-4 bg-secondary-50 rounded-lg">
                <div className="flex items-center space-x-3 mb-2">
                  <CreditCard className="w-5 h-5 text-primary-600" />
                  <span className="font-medium text-secondary-900">Secure Payment</span>
                </div>
                <p className="text-sm text-secondary-600">
                  We'll contact you to arrange payment and discuss custom details.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Checkout; 